import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { approveBudget } from '../services/budgetService';
import api from '../services/api';
import { useError } from '../contexts/ErrorContext';
import { colors } from '../styles';
import BudgetCategoryMiniCard from './BudgetCategoryMiniCard';
import { getCategoryIcon } from '../utils/categoryIcons';

const FullScreenDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    margin: 0,
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: 0,
    backgroundColor: theme.palette.background.default
  }
}));

const ProgressIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  marginBottom: theme.spacing(2)
}));

const NavigationButton = styled(IconButton)(({ theme, disabled }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  ...(disabled && {
    opacity: 0.3,
    cursor: 'not-allowed'
  })
}));

const TransactionItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  borderRadius: '12px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`
}));

const CategoryReviewModal = ({ open, onClose, budgetId, allCategoryIds = [] }) => {
  const { showError, showSuccess } = useError();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [approved, setApproved] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);

  useEffect(() => {
    if (open && budgetId) {
      loadCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, budgetId]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/budgets/${budgetId}/categories/review`);
      if (response.data.success && response.data.data.categories) {
        setCategories(response.data.data.categories);
        setCurrentIndex(0);
        setApproved(false);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      showError('Failed to load categories for review');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowTooltip(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < categories.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowTooltip(false);
    }
  };

  const handleAccept = () => {
    // Mark category as reviewed (could store this in state if needed)
    handleNext();
  };

  const handleAdjust = () => {
    // Navigate to budget page with category highlighted using React Router
    const currentCategory = categories[currentIndex];
    if (!currentCategory?.id) {
      showError('Unable to navigate: category information is missing');
      return;
    }
    navigate(`/budgets?budgetId=${budgetId}&categoryId=${currentCategory.id}`);
  };

  const handleDoneReviewing = async () => {
    try {
      setLoading(true);
      await approveBudget(budgetId);
      setApproved(true);
      showSuccess('Budget approved successfully!');
      // Close modal immediately - parent component handles state refresh via onClose callback
      // The success message will remain visible via the snackbar/toast system
      onClose();
    } catch (error) {
      console.error('Error approving budget:', error);
      const code = error?.response?.data?.code;
      const msg = error?.response?.data?.error || 'Failed to approve budget';
      const txIds = error?.response?.data?.data?.transactionIds;
      if (code === 'LOW_CONFIDENCE_REVIEW_REQUIRED' && Array.isArray(txIds) && txIds.length > 0) {
        showError(msg);
        return;
      }
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const currentCategory = categories[currentIndex];
  const isFirstCategory = currentIndex === 0;
  const isLastCategory = currentIndex === categories.length - 1;

  if (!open) return null;

  return (
    <FullScreenDialog open={open} onClose={approved ? onClose : undefined} maxWidth={false}>
      <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Review Budget Categories
          </Typography>
          {approved && (
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        {loading && !categories.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : currentCategory ? (
          <>
            {/* Progress Indicator */}
            <Box sx={{ p: 3, pb: 2 }}>
              <ProgressIndicator>
                <Typography variant="body2" color="text.secondary">
                  Category {currentIndex + 1} of {categories.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {categories.map((_, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: idx <= currentIndex ? colors.primary : 'rgba(0,0,0,0.1)',
                        transition: 'background-color 0.2s'
                      }}
                    />
                  ))}
                </Box>
              </ProgressIndicator>

              {/* Educational Tooltip */}
              {showTooltip && isFirstCategory && (
                <Tooltip
                  open={showTooltip}
                  title="Review each category and its transactions. Adjust if needed."
                  arrow
                  placement="top"
                  onClose={() => setShowTooltip(false)}
                >
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: '12px' }}>
                    <Typography variant="body2" color="info.contrastText">
                      💡 Review each category and its transactions. Adjust if needed.
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>

            {/* Category Content */}
            {currentCategory && (
              <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 3 }}>
                {/* Category Name and Icon */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    {getCategoryIcon(currentCategory.icon, { sx: { fontSize: 48, color: colors.primary } })}
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {currentCategory.name}
                  </Typography>
                </Box>

              {/* Budget Ring */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <BudgetCategoryMiniCard
                  name={currentCategory.name}
                  budgeted={currentCategory.budgeted}
                  spent={currentCategory.spent}
                  icon={getCategoryIcon(currentCategory.icon)}
                  width={220}
                  height={280}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mb: 4, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleAdjust}
                  sx={{ flex: 1, maxWidth: 200 }}
                >
                  Adjust
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleAccept}
                  sx={{ flex: 1, maxWidth: 200 }}
                >
                  Accept
                </Button>
              </Box>

              {/* Transactions List */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Transactions ({currentCategory.transactionCount})
              </Typography>
              <Box>
                {currentCategory.sampleTransactions && currentCategory.sampleTransactions.length > 0 ? (
                  currentCategory.sampleTransactions.map((transaction) => (
                    <TransactionItem key={transaction.id}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {transaction.description || transaction.merchantName || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(transaction.authorizedDate || transaction.date).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: transaction.amount > 0 ? colors.status.error : colors.status.success
                        }}
                      >
                        {transaction.amount > 0 ? '-' : ''}
                        ${Math.abs(transaction.amount).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </Typography>
                    </TransactionItem>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No transactions in this category yet
                  </Typography>
                )}
              </Box>
              </Box>
            )}

            {/* Navigation Footer */}
            <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <NavigationButton onClick={handlePrevious} disabled={isFirstCategory}>
                <ArrowBackIcon />
              </NavigationButton>

              {isLastCategory ? (
                <Button
                  variant="contained"
                  onClick={handleDoneReviewing}
                  disabled={loading}
                  sx={{ px: 4 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Done Reviewing'}
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Swipe or use arrows to navigate
                </Typography>
              )}

              <NavigationButton onClick={handleNext} disabled={isLastCategory}>
                <ArrowForwardIcon />
              </NavigationButton>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography variant="body1" color="text.secondary">
              No categories found
            </Typography>
          </Box>
        )}
      </DialogContent>
    </FullScreenDialog>
  );
};

export default CategoryReviewModal;

